/**
 * ThinkingBlock — §1.2 Thinking/Reasoning Display
 *
 * Renders <think>/<thinking>/<reason>/<reasoning> tags from reasoning models
 * (Qwen 3, etc.) as collapsible sections with timing.
 *
 * During streaming: "Thinking..." with animated spinner
 * After completion: "Thought for N seconds" with expand/collapse
 */

import { useState, useEffect, useRef } from 'react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { ChevronRight, Brain } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ThinkingBlockProps {
  content: string;
  isStreaming?: boolean;
}

export function ThinkingBlock({ content, isStreaming = false }: ThinkingBlockProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!isStreaming) return;
    const interval = setInterval(() => {
      setElapsed(Math.round((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isStreaming]);

  useEffect(() => {
    // When streaming stops, compute the FINAL elapsed time. The previous
    // `elapsed === 0` guard was unsatisfiable once the 1s interval had ticked
    // (elapsed > 0 forever), so the displayed total froze at the last whole-
    // second tick instead of the true end time. Runs only on the isStreaming
    // transition (deps=[isStreaming]); `elapsed` is intentionally not a dep, so
    // this cannot loop.
    if (!isStreaming) {
      setElapsed(Math.round((Date.now() - startRef.current) / 1000));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  const label = isStreaming
    ? `Thinking... (${elapsed}s)`
    : `Thought for ${elapsed}s`;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="my-2">
      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer group">
        <Brain className={cn('h-4 w-4', isStreaming && 'animate-pulse text-blue-500')} />
        <span>{label}</span>
        <ChevronRight className={cn(
          'h-3 w-3 transition-transform duration-200',
          isOpen && 'rotate-90'
        )} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 pl-6 border-l-2 border-muted">
        <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
          {content}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Thinking Tag Parser ─────────────────────────────────────────────────────

const THINK_OPEN = /<(?:think|thinking|reason|reasoning)>/i;
const THINK_CLOSE = /<\/(?:think|thinking|reason|reasoning)>/i;

interface ParsedContent {
  type: 'text' | 'thinking';
  content: string;
}

/**
 * Parse streaming content into text and thinking blocks.
 * Handles partial tags during streaming.
 */
export function parseThinkingBlocks(content: string): ParsedContent[] {
  const parts: ParsedContent[] = [];
  let remaining = content;

  while (remaining.length > 0) {
    const openMatch = THINK_OPEN.exec(remaining);
    if (!openMatch) {
      // No more thinking tags
      if (remaining.trim()) {
        parts.push({ type: 'text', content: remaining });
      }
      break;
    }

    // Add text before the tag
    const beforeTag = remaining.substring(0, openMatch.index);
    if (beforeTag.trim()) {
      parts.push({ type: 'text', content: beforeTag });
    }

    // Find closing tag
    const afterOpen = remaining.substring(openMatch.index + openMatch[0].length);
    const closeMatch = THINK_CLOSE.exec(afterOpen);

    if (closeMatch) {
      // Complete thinking block
      const thinkContent = afterOpen.substring(0, closeMatch.index);
      parts.push({ type: 'thinking', content: thinkContent.trim() });
      remaining = afterOpen.substring(closeMatch.index + closeMatch[0].length);
    } else {
      // Unclosed tag (still streaming) — treat remaining as thinking content
      parts.push({ type: 'thinking', content: afterOpen.trim() });
      break;
    }
  }

  return parts;
}

/**
 * Strip thinking tags from content (for copying, citations).
 */
export function stripThinkingTags(content: string): string {
  return content
    .replace(/<(?:think|thinking|reason|reasoning)>[\s\S]*?<\/(?:think|thinking|reason|reasoning)>/gi, '')
    .trim();
}
